import { CardActions, Grid, useEventCallback } from '@mui/material';
import { Card, Typography } from '@mui/material';
import {
  DetailMunicipalityCrumbs,
  EditMunicipalityCrumbs,
  NewMunicipalityCrumbs,
} from 'civil-status/constants/breadcrumb';
import { Fragment, ReactElement, useEffect, useMemo } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { PmiButton } from 'shell/components/shared/button';
import { Form, Formik } from 'formik';
import { CardContent } from '@mui/material';
import { MunicipalityFormVM } from 'civil-status/view-models/municipality-form';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import FormMunicipality from 'civil-status/components/municipality';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ID_DOMINIO_COMUNI } from '../research-municipality';
import { transformerMunicipalityData } from 'civil-status/utils/transformer-municipality-data';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformerMunicipalityToSave } from 'civil-status/utils/transformer-municipality-to-save';
import { ValueDomainType } from 'shell/models/value-domain-type';

const validationSchema = yup.object({
  institution: yup.string().nullable().required('Il campo è obbligatorio'),
  id: yup.string().nullable().required('Il campo è obbligatorio'),
  idAnsc: yup.string().nullable().required('Il campo è obbligatorio'),
  anscMunicipality: yup.object().nullable().shape({}).required('Il campo è obbligatorio'),
  dateFrom: yup.date().nullable().typeError('Data non valida'),
  dateTo: yup
    .date()
    .nullable()
    .typeError('Data non valida')
    .when('dateFrom', (dateFrom, formvalidationSchema) =>
      dateFrom && dateFrom?.toString() !== 'Invalid Date'
        ? formvalidationSchema.min(
            dateFrom,
            'La data deve essere maggiore o uguale della data Inizio validità'
          )
        : formvalidationSchema.nullable()
    ),
  description: yup.string().required('Il campo è obbligatorio'),
});

interface MunicipalityProps {
  isEdit?: boolean;
  isDeatil?: boolean;
}

// NEW is default
const Municipality = ({ isEdit = false, isDeatil = false }: MunicipalityProps): ReactElement => {
  const { userProfile } = useUserProfile();

  const navigate = useNavigate();

  const params = useParams();

  const idValore = params.idValore || '';

  const [municipalityData, getMunicipalityData] = useHttpFetch<
    HttpResponse<ValueDomainType>,
    MunicipalityFormVM
  >(`${__ACT_API_URL__}/valori-dominio-atti/${ID_DOMINIO_COMUNI}/${idValore}`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerMunicipalityData,
  });

  useEffect(() => {
    if ((isEdit || isDeatil) && idValore) {
      getMunicipalityData();
    }
  }, [isEdit, isDeatil, idValore]);

  const onBack = useEventCallback(() => {
    navigate('/stato-civile/ricerca-comune');
  });

  const insertData = useHttpClient(`${__ACT_API_URL__}/valori-dominio-atti/insert`, {
    options: { method: 'POST' },
  });

  const updateData = useHttpClient(`${__ACT_API_URL__}/valori-dominio-atti/update`, {
    options: {
      errorCatching: true,
      method: 'PUT',
      loading: true,
    },
  });

  const onSubmit = useEventCallback((values: MunicipalityFormVM) => {
    const data = transformerMunicipalityToSave(values || {});

    if (isEdit) {
      (updateData({ data }) as ResponsePromise<void>).then(() => {
        toast.success('Operazione eseguita con successo');
        onBack();
      });
    } else {
      (insertData({ data }) as ResponsePromise<void>).then(() => {
        toast.success('Operazione eseguita con successo');
        onBack();
      });
    }
  });

  const title: string = useMemo(() => {
    if (isEdit) {
      return 'Modifica Comune';
    }

    if (isDeatil) {
      return 'Visualizza Comune';
    }

    return 'Inserimento Comune';
  }, [isEdit, isDeatil]);

  const BreadcrumbsCrumbs = useMemo(() => {
    if (isEdit) {
      return EditMunicipalityCrumbs;
    }

    if (isDeatil) {
      return DetailMunicipalityCrumbs;
    }

    return NewMunicipalityCrumbs;
  }, [isEdit, isDeatil]);

  const initialValues: MunicipalityFormVM = useMemo(() => {
    if ((isEdit || isDeatil) && municipalityData.data) {
      return municipalityData.data as MunicipalityFormVM;
    }

    return {
      institution: userProfile.datiLogin?.key?.codiceEnte || '',
      id: '',
      dateFrom: null,
      dateTo: null,
      order: undefined,
      idAnsc: '',
      description: '',
      note: '',
      anscMunicipality: null,
    } as MunicipalityFormVM;
  }, [userProfile, isEdit, isDeatil, municipalityData.data]);

  return (
    <Fragment>
      <Breadcrumbs items={BreadcrumbsCrumbs} />
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnMount
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        <Form>
          <Card raised className="pmi-my-3">
            <CardContent style={{ padding: '24px' }}>
              <Typography
                color="secondary.main"
                style={{
                  fontSize: '26px',
                }}
              >
                {title}
              </Typography>
              <div style={{ padding: '24px 0 0 0' }}>
                <FormMunicipality isEdit={isEdit} isDetail={isDeatil} disabled={isDeatil} />
              </div>
            </CardContent>
            <CardActions style={{ padding: '24px 24px 16px 24px' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <PmiButton
                    fullWidth
                    variant="outlined"
                    color="cdmBrandThird"
                    type="submit"
                    disabled={isDeatil}
                  >
                    Conferma
                  </PmiButton>
                </Grid>
                <Grid item xs={12} md={2}>
                  <PmiButton fullWidth variant="outlined" color="cdmBrand" onClick={onBack}>
                    Annulla
                  </PmiButton>
                </Grid>
              </Grid>
            </CardActions>
          </Card>
        </Form>
      </Formik>
    </Fragment>
  );
};

Municipality.displayName = 'Municipality Page component';

export default Municipality;
